import React, { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';

import { Box } from '@mui/material';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import AutoLockSubsection from 'app/pages/loanApplication/AutoLockSubsection';
import {
  AutoLockFormsLevelType,
  FinancialInfoSubsectionNames,
} from 'app/services/autoLockForms/autoLockForms.constants';
import useEnsureChannelMessagesLoaded from 'app/shared/components/TeamChat/hooks/useEnsureChannelMessagesLoaded';
import useLoanChatTargets from 'app/shared/components/TeamChat/hooks/useLoanChatTargets';
import clsx from 'clsx';
import { WithLoadingPropTypes } from 'hocs/withLoading';

import { selectFinancialInfoPendingChanges } from 'store/loanApplications/selectors';

import {
  FINANCIAL_INFO_ACTION_TYPE,
  FINANCIAL_INFO_TABLE_KEY,
  FINANCIAL_INFO_VIEWS_PATHS,
  NEW_RECORD_DATA,
} from '../FinancialInfo.constants';
import { generateFinancialInfoPath } from '../FinancialInfo.utils';

import Assets from './Assets';
import EmploymentAndIncome from './EmploymentAndIncome';
import { wrapperStyles } from './FinancialInfoBody.styles';
import Liabilities from './Liabilities';
import Reo from './Reo';

const FinancialInfoBody = ({ isLoading }) => {
  const { subtab, applicationId, tab, entityId: borrowerId } = useParams();
  const history = useHistory();

  const { pathname: pathFrom } = useLocation();

  const isExpanded = subtab !== FINANCIAL_INFO_VIEWS_PATHS.ALL_VIEWS;
  const pendingChanges = useSelector(selectFinancialInfoPendingChanges);
  const chatTargets = useLoanChatTargets(applicationId);

  useEnsureChannelMessagesLoaded(applicationId);

  const onToggle = useCallback(
    tableName => {
      const path = LoansPaths.applicationManagement.generatePath({
        applicationId,
        tab,
        subtab: tableName,
        entityId: borrowerId,
      });

      history.push(path);
    },
    [applicationId, borrowerId, tab, history],
  );

  const onAddRecord = useCallback(
    tablePath => {
      const to = generateFinancialInfoPath({
        applicationId,
        tab,
        tablePath,
        borrowerId,
        actionType: FINANCIAL_INFO_ACTION_TYPE.EDIT,
        recordId: NEW_RECORD_DATA.id,
        pathFrom,
      });

      history.push(to);
    },
    [applicationId, tab, borrowerId, pathFrom, history],
  );

  return (
    <Box className={clsx({ expanded: isExpanded })} sx={wrapperStyles}>
      <AutoLockSubsection
        areaName={FinancialInfoSubsectionNames.EMPLOYMENT_INFO}
        levelType={AutoLockFormsLevelType.SUBSECTION}
      >
        <EmploymentAndIncome
          borrowerId={borrowerId}
          chatTargets={chatTargets}
          isLoading={isLoading}
          pendingChanges={
            pendingChanges[
              FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.INCOME_AND_EMPLOYMENT]
            ]
          }
          tableName={FINANCIAL_INFO_VIEWS_PATHS.INCOME_AND_EMPLOYMENT}
          onAddRecord={onAddRecord}
          onToggle={onToggle}
        />
      </AutoLockSubsection>
      <AutoLockSubsection
        areaName={FinancialInfoSubsectionNames.LIABILITIES}
        levelType={AutoLockFormsLevelType.SUBSECTION}
      >
        <Liabilities
          borrowerId={borrowerId}
          chatTargets={chatTargets}
          isLoading={isLoading}
          pendingChanges={
            pendingChanges[FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES]]
          }
          tableName={FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES}
          onAddRecord={onAddRecord}
          onToggle={onToggle}
        />
      </AutoLockSubsection>
      <AutoLockSubsection
        areaName={FinancialInfoSubsectionNames.REO}
        levelType={AutoLockFormsLevelType.SUBSECTION}
      >
        <Reo
          borrowerId={borrowerId}
          chatTargets={chatTargets}
          isLoading={isLoading}
          pendingChanges={pendingChanges[FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.REO]]}
          tableName={FINANCIAL_INFO_VIEWS_PATHS.REO}
          onAddRecord={onAddRecord}
          onToggle={onToggle}
        />
      </AutoLockSubsection>
      <AutoLockSubsection
        areaName={FinancialInfoSubsectionNames.ASSETS}
        levelType={AutoLockFormsLevelType.SUBSECTION}
      >
        <Assets
          borrowerId={borrowerId}
          chatTargets={chatTargets}
          isLoading={isLoading}
          pendingChanges={
            pendingChanges[FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.ASSETS]]
          }
          tableName={FINANCIAL_INFO_VIEWS_PATHS.ASSETS}
          onAddRecord={onAddRecord}
          onToggle={onToggle}
        />
      </AutoLockSubsection>
    </Box>
  );
};

FinancialInfoBody.propTypes = {
  ...WithLoadingPropTypes,
};

export default memo(FinancialInfoBody);
